import React from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import type { DashboardOverviewAnalyticsResponse } from '@/lib/api/dashboard';
import dashboardClientActivityUserIcon from '@/assets/images/dashboard-client-activity-user.svg';

import { CS_PRIMARY } from './dashboardHomeTokens';
import { formatDashboardCount } from './dashboardHomeFormatters';

export interface DashboardAnalyticsClientActivityCardProps {
  data?: DashboardOverviewAnalyticsResponse;
  isLoading?: boolean;
  isCustomDateRange?: boolean;
  onViewAllClients?: () => void;
}

const conditionColors = ['#ffa2a2', '#ff9f43', '#2ec3b6', '#7c5cbf', '#3f9fee'];

export const DashboardAnalyticsClientActivityCard: React.FC<DashboardAnalyticsClientActivityCardProps> = ({
  data,
  isLoading = false,
  isCustomDateRange = false,
  onViewAllClients,
}) => {
  const activity = data?.clientActivity;
  const conditions = activity?.conditions ?? [];
  const maxConditionCount = Math.max(...conditions.map((c) => c.count), 1);

  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: '14px',
        border: '1px solid #e5e7eb',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box component="img" src={dashboardClientActivityUserIcon} alt="" aria-hidden sx={{ width: 24, height: 24, objectFit: 'contain', display: 'block' }} />
          <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#1e2939', letterSpacing: '-0.01em' }}>
            Client Condition
          </Typography>
        </Stack>
        <Button
          variant="text"
          size="small"
          onClick={onViewAllClients}
          endIcon={<ChevronRightRoundedIcon sx={{ fontSize: 15 }} />}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 14,
            color: CS_PRIMARY,
            minWidth: 0,
            px: 0.5,
            '&:hover': { bgcolor: 'rgba(30, 174, 219, 0.06)' },
          }}
        >
          View all
        </Button>
      </Stack>

      <Stack direction="row" spacing={1.5} useFlexGap sx={{ width: '100%' }}>
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: '16px',
            border: '1px solid #ebebeb',
            p: 2,
            minHeight: 82.5,
          }}
        >
          <Typography sx={{ fontWeight: 400, fontSize: 12, color: '#000' }}>Active</Typography>
          <Typography sx={{ fontWeight: 500, fontSize: 20, color: '#00bba7', mt: 0.5, letterSpacing: '-0.03em' }}>
            {isLoading && !data ? '--' : formatDashboardCount(activity?.active)}
          </Typography>
        </Paper>
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: '16px',
            border: '1px solid rgba(0, 187, 167, 0.62)',
            p: 2,
            minHeight: 82.5,
          }}
        >
          <Typography sx={{ fontWeight: 400, fontSize: 12, color: '#000' }}>
            {isCustomDateRange ? 'New in range' : 'Last 30 days'}
          </Typography>
          <Typography sx={{ fontWeight: 500, fontSize: 20, color: '#00bba7', mt: 0.5, letterSpacing: '-0.03em' }}>
            {isLoading && !data ? '--' : formatDashboardCount(activity?.newThisMonth)}
          </Typography>
        </Paper>
      </Stack>

      <Typography sx={{ fontWeight: 500, fontSize: 14, color: '#000', letterSpacing: '-0.01em' }}>
        Client Condition
      </Typography>

      <Stack spacing={0}>
        {isLoading && conditions.length === 0 && (
          <Typography sx={{ px: 1.75, py: 1.25, fontSize: 12, color: '#4a5565' }}>Loading client condition...</Typography>
        )}
        {!isLoading && conditions.length === 0 && (
          <Typography sx={{ px: 1.75, py: 1.25, fontSize: 12, color: '#4a5565' }}>No active condition data yet.</Typography>
        )}
        {conditions.map((row, index) => (
          <Stack
            key={row.name}
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ px: 1.75, py: 1.25, borderRadius: '14px' }}
          >
            <Typography sx={{ fontWeight: 500, fontSize: 13, color: '#1a1a1a', width: 126, flexShrink: 0 }}>
              {row.name}
            </Typography>
            <Box sx={{ flex: 1, height: 8, bgcolor: '#f3f4f6', borderRadius: 999, overflow: 'hidden', minWidth: 0 }}>
              <Box
                sx={{
                  width: `${(row.count / maxConditionCount) * 100}%`,
                  height: '100%',
                  bgcolor: conditionColors[index % conditionColors.length],
                  borderRadius: 999,
                }}
              />
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#1a1a1a', width: 40, textAlign: 'right', flexShrink: 0 }}>
              {row.count}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};
